import { AcquirerType, IRestaurantBankSettingsAcquirer, TYandexPaymentType } from 'types';
import {
	RSB_SBP_QR_BANK_SETTINGS_ID,
	YANDEX_PAY_QR_BANK_SETTINGS_ID,
	VIVA_WALLET_SOFT_POS_QR_BANK_SETTINGS_ID,
	BEST_2_PAY_QR_BANK_SETTINGS_ID,
	YANDEX_CHECK_QR_BANK_SETTINGS_ID,
	ADYEN_BANK_SETTINGS_ID,
	TOUCAN_BANK_SETTINGS_ID,
	TBANK_BANK_SETTINGS_ID,
} from 'appConstants';
import { TTypeOfModalWindow } from '../../../../../bankSettingsState/bankSettingsContext';
import YandexPaymentTypeSelect from './components/YandexPaymentTypeSelect';
import VivaApiSecretField from './components/VivaApiSecretField';
import VivaApiKeyField from './components/VivaApiKeyField';
import AccNumberField from './components/AccNumberField';
import SectorField from './components/SectorField';
import SdRefField from './components/SdRefField';
import YandexChequeApiTokenField from './components/YandexChequeApiTokenField';
import YandexChequeApiPartnerIdField from './components/YandexChequeApiPartnerIdField';
import { MerchantAccountField } from './components/MerchantAccountField';
import { StoreField } from './components/StoreField';
import { XApiKeyField } from './components/XApiKeyField';
import { CheckoutUrlPrefixField } from './components/CheckoutUrlPrefixField';
import { SplitPayments3SCheckbox } from './components/SplitPayments3SCheckbox';
import { MerchantBalanceAccountIdField } from './components/MerchantBalanceAccountIdField';
import { KmBalanceAccountIdField } from './components/KmBalanceAccountIdField';
import FileBlock from './components/FileBlock/FileBlock';
import AccountNumberField from './components/AccountNumberField';
import LoginField from './components/LoginField';
import MerchantIdField from './components/MerchantIdField';
import MerchantNameField from './components/MerchantNameField';
import PriorityField from './components/PriorityField';
import { TypeSelect } from './components/TypeSelect';
import PaymentTypeIdField from './components/PaymentTypeIdField';
import IsFeeFiscalizedCheckbox from './components/IsFeeFiscalizedCheckbox';
import DisplayTipsCheckbox from './components/DisplayTipsCheckbox';
import { GenerateQrInstantlySwitch } from './components/GenerateQrInstantlySwitch';
import React, { useCallback } from 'react';
import breadcrumbs from 'assets/img/breadcrumbs.svg';
import YandexApiKeyField from './components/YandexApiKeyField';
import SmsCodeField from './components/SmsCodeField';
import { TBankPlaceIdField } from './components/TBankPlaceIdField';
import { MasterWalletField } from './components/MasterWalletField';
import style from './BankSettingFields.module.scss';

interface IProps {
	settingsId: string;
	acquirerId: string | undefined;
	acquirerType: AcquirerType | undefined;
	bankSettingsFilePath: string | null;
	logoFilePath: string | null;
	isTipsSupported: boolean;
	generateQrInstantly: boolean;
	isGenerateQrInstantlyDisabled: boolean;
	isFeeFiscalized: boolean;
	splitPayments3S: boolean;
	selectedYandexPaymentTypes: TYandexPaymentType[];
	fetchedAcquirers: IRestaurantBankSettingsAcquirer[];
	changePriority: (settingsId: string, value: string) => void;
	changeMerchantId: (settingsId: string, value: string) => void;
	changeMerchantName: (settingsId: string, value: string) => void;
	changeAcquirer: (settingsId: string, value: IRestaurantBankSettingsAcquirer | null) => void;
	changeSbpAccountNumber: (settingsId: string, value: string) => void;
	changeSbpLogin: (settingsId: string, value: string) => void;
	changeYandexPaymentType: (settingsId: string, value: TYandexPaymentType[]) => void;
	changeYandexApiKey: (settingsId: string, value: string) => void;
	changeVivaApiKey: (settingsId: string, value: string) => void;
	changeVivaApiSecret: (settingsId: string, value: string) => void;
	changeAccNumber: (settingsId: string, value: string) => void;
	changeSector: (settingsId: string, value: string) => void;
	changeSdRef: (settingsId: string, value: string) => void;
	changeYandexChequeApiToken: (settingsId: string, value: string) => void;
	changeYandexChequeApiPartnerId: (settingsId: string, value: string) => void;
	changeMerchantAccount: (settingsId: string, value: string) => void;
	changeStore: (settingsId: string, value: string) => void;
	changeXApiKey: (settingsId: string, value: string) => void;
	changeCheckoutUrlPrefix: (settingsId: string, value: string) => void;
	toggleSplitPayments3S: (settingsId: string) => void;
	changeMerchantBalanceAccountId: (settingsId: string, value: string) => void;
	changeKmBalanceAccountId: (settingsId: string, value: string) => void;
	toggleDisplayTips: (settingsId: string) => void;
	toggleIsFeeFiscalized: (settingsId: string) => void;
	toggleGenerateQrInstantly: (settingsId: string) => void;
	changePaymentTypeId: (settingsId: string, value: string) => void;
	changeSmsCode: (settingsId: string, value: string) => void;
	changeTBankPlaceId: ({ settingsId, value }: { settingsId: string; value: string }) => void;
	changeMasterWallet: ({ settingsId, value }: { settingsId: string; value: string }) => void;
	deleteBankSettingBlock: (value: string) => void;
	setFetchedAcquirers: (value: IRestaurantBankSettingsAcquirer[]) => void;
	setBankSettingFile: (settingsId: string, value: string | null) => void;
	setLogoFile: (settingsId: string, value: string | null) => void;
	deleteBankSettingFile: (value: string) => void;
	changeTypeOfModalWindow: (value: TTypeOfModalWindow) => void;
}

export const BankSettingFields: React.FC<IProps> = ({
	settingsId,
	acquirerId,
	acquirerType,
	bankSettingsFilePath,
	logoFilePath,
	setBankSettingFile,
	setLogoFile,
	changeAcquirer,
	changeMerchantId,
	changeMerchantName,
	changePriority,
	changeSbpAccountNumber,
	changeSbpLogin,
	changeYandexPaymentType,
	changeYandexApiKey,
	changeVivaApiKey,
	changeVivaApiSecret,
	changeAccNumber,
	changeSector,
	changeSdRef,
	changeYandexChequeApiToken,
	changeYandexChequeApiPartnerId,
	changeMerchantAccount,
	changeStore,
	changeXApiKey,
	changeCheckoutUrlPrefix,
	toggleSplitPayments3S,
	changeMerchantBalanceAccountId,
	changeKmBalanceAccountId,
	toggleDisplayTips,
	toggleIsFeeFiscalized,
	toggleGenerateQrInstantly,
	changePaymentTypeId,
	changeSmsCode,
	changeTBankPlaceId,
	changeMasterWallet,
	isTipsSupported,
	generateQrInstantly,
	isGenerateQrInstantlyDisabled,
	isFeeFiscalized,
	splitPayments3S,
	fetchedAcquirers,
	setFetchedAcquirers,
	deleteBankSettingBlock,
	deleteBankSettingFile,
	selectedYandexPaymentTypes,
	changeTypeOfModalWindow,
}) => {
	const handleChangePriority = useCallback(
		(value: string) => {
			changePriority(settingsId, value);
		},
		[settingsId, changePriority],
	);

	const handleChangeMerchantId = useCallback(
		(value: string) => {
			changeMerchantId(settingsId, value);
		},
		[settingsId, changeMerchantId],
	);

	const handleChangeMerchantName = useCallback(
		(value: string) => {
			changeMerchantName(settingsId, value);
		},
		[settingsId, changeMerchantName],
	);

	const handleChangeAcquirer = useCallback(
		(value: string | null) => {
			const acquirer = fetchedAcquirers.find((el) => el.id === value);
			acquirer && changeAcquirer(settingsId, acquirer);
		},
		[settingsId, fetchedAcquirers, changeAcquirer],
	);

	const handleChangePaymentTypeId = useCallback(
		(value: string) => {
			changePaymentTypeId(settingsId, value);
		},
		[settingsId, changePaymentTypeId],
	);

	const handleChangeSmsCode = useCallback(
		(value: string) => {
			changeSmsCode(settingsId, value);
		},
		[settingsId, changeSmsCode],
	);

	const handleChangeTBankPlaceId = useCallback(
		(value: string) => {
			changeTBankPlaceId({ settingsId, value });
		},
		[settingsId, changeTBankPlaceId],
	);

	const handleChangeMasterWallet = useCallback(
		(value: string) => {
			changeMasterWallet({ settingsId, value });
		},
		[settingsId, changeMasterWallet],
	);

	const handleChangeIsFeeFiscalized = useCallback(() => {
		toggleIsFeeFiscalized(settingsId);
	}, [settingsId, toggleIsFeeFiscalized]);

	const handleChangeDisplayTips = useCallback(() => {
		toggleDisplayTips(settingsId);
	}, [settingsId, toggleDisplayTips]);

	const handleChangeGenerateQrInstantly = useCallback(() => {
		toggleGenerateQrInstantly(settingsId);
	}, [settingsId, toggleGenerateQrInstantly]);

	const handleChangeSbpAccountNumber = useCallback(
		(value: string) => {
			changeSbpAccountNumber(settingsId, value);
		},
		[settingsId, changeSbpAccountNumber],
	);

	const handleChangeSbpLogin = useCallback(
		(value: string) => {
			changeSbpLogin(settingsId, value);
		},
		[settingsId, changeSbpLogin],
	);

	const handleChangeYandexPaymentType = useCallback(
		(value: TYandexPaymentType[]) => {
			changeYandexPaymentType(settingsId, value);
		},
		[settingsId, changeYandexPaymentType],
	);

	const handleChangeYandexApiKey = useCallback(
		(value: string) => {
			changeYandexApiKey(settingsId, value);
		},
		[settingsId, changeYandexApiKey],
	);

	const handleChangeVivaApiKey = useCallback(
		(value: string) => {
			changeVivaApiKey(settingsId, value);
		},
		[settingsId, changeVivaApiKey],
	);

	const handleChangeVivaApiSecret = useCallback(
		(value: string) => {
			changeVivaApiSecret(settingsId, value);
		},
		[settingsId, changeVivaApiSecret],
	);

	const handleChangeAccNumber = useCallback(
		(value: string) => {
			changeAccNumber(settingsId, value);
		},
		[settingsId, changeAccNumber],
	);

	const handleChangeSector = useCallback(
		(value: string) => {
			changeSector(settingsId, value);
		},
		[settingsId, changeSector],
	);

	const handleChangeSdRef = useCallback(
		(value: string) => {
			changeSdRef(settingsId, value);
		},
		[settingsId, changeSdRef],
	);

	const handleChangeYandexChequeApiToken = useCallback(
		(value: string) => {
			changeYandexChequeApiToken(settingsId, value);
		},
		[settingsId, changeYandexChequeApiToken],
	);

	const handleChangeYandexChequeApiPartnerId = useCallback(
		(value: string) => {
			changeYandexChequeApiPartnerId(settingsId, value);
		},
		[settingsId, changeYandexChequeApiPartnerId],
	);

	const handleChangeMerchantAccount = useCallback(
		(value: string) => {
			changeMerchantAccount(settingsId, value);
		},
		[settingsId, changeMerchantAccount],
	);

	const handleChangeStore = useCallback(
		(value: string) => {
			changeStore(settingsId, value);
		},
		[settingsId, changeStore],
	);

	const handleChangeXApiKey = useCallback(
		(value: string) => {
			changeXApiKey(settingsId, value);
		},
		[settingsId, changeXApiKey],
	);

	const handleCheckoutUrlPrefix = useCallback(
		(value: string) => {
			changeCheckoutUrlPrefix(settingsId, value);
		},
		[settingsId, changeCheckoutUrlPrefix],
	);

	const handleToggleSplitPayments3S = useCallback(() => {
		toggleSplitPayments3S(settingsId);
	}, [settingsId, toggleSplitPayments3S]);

	const handleChangeMerchantBalanceAccountId = useCallback(
		(value: string) => {
			changeMerchantBalanceAccountId(settingsId, value);
		},
		[settingsId, changeMerchantBalanceAccountId],
	);

	const handleChangeKmBalanceAccountId = useCallback(
		(value: string) => {
			changeKmBalanceAccountId(settingsId, value);
		},
		[settingsId, changeKmBalanceAccountId],
	);

	const handleDeleteBlock = useCallback(() => {
		deleteBankSettingBlock(settingsId);
	}, [settingsId, deleteBankSettingBlock]);

	const handleImageFileSizeExceeded = useCallback(() => {
		changeTypeOfModalWindow('imageFileSizeExceeded');
	}, [changeTypeOfModalWindow]);

	return (
		<div className={style.fieldsWrapper}>
			<div className={style.closeBtn} onClick={handleDeleteBlock}>
				<svg className={style.closeIcon}>
					<use xlinkHref={`${breadcrumbs}#crossMark`} />
				</svg>
			</div>
			<div className={style.fields}>
				<div className={style.row}>
					<PriorityField settingsId={settingsId} onChange={handleChangePriority} />
					<MerchantIdField settingsId={settingsId} onChange={handleChangeMerchantId} />
				</div>
				<div className={style.row}>
					<MerchantNameField settingsId={settingsId} onChange={handleChangeMerchantName} />
					<TypeSelect
						settingsId={settingsId}
						onChange={handleChangeAcquirer}
						setFetchedAcquirers={setFetchedAcquirers}
					/>
				</div>
				<div className={style.row}>
					<div className={style.flex1}>
						<IsFeeFiscalizedCheckbox
							settingsId={settingsId}
							isFeeFiscalized={isFeeFiscalized}
							onToggleIsFeeFiscalized={handleChangeIsFeeFiscalized}
						/>
					</div>
					<div className={style.flex1}>
						<div className="flex gap-2.5">
							<div className="flex-1">
								<DisplayTipsCheckbox
									settingsId={settingsId}
									isTipsSupported={isTipsSupported}
									onToggleDisplayTips={handleChangeDisplayTips}
								/>
							</div>
							{acquirerType === AcquirerType.QR && (
								<div className="flex-1">
									<GenerateQrInstantlySwitch
										generateQrInstantly={generateQrInstantly}
										isDisabled={isGenerateQrInstantlyDisabled}
										onToggleGenerateQrInstantly={handleChangeGenerateQrInstantly}
									/>
								</div>
							)}
						</div>
					</div>
				</div>
				{acquirerId !== TBANK_BANK_SETTINGS_ID && (
					<div className={style.row}>
						<PaymentTypeIdField settingsId={settingsId} onChange={handleChangePaymentTypeId} />
						{acquirerId === TOUCAN_BANK_SETTINGS_ID ? (
							<SmsCodeField settingsId={settingsId} onChange={handleChangeSmsCode} />
						) : (
							<div className={style.flex1} />
						)}
					</div>
				)}
				{acquirerId === RSB_SBP_QR_BANK_SETTINGS_ID && (
					<div className={style.row}>
						<AccountNumberField settingsId={settingsId} onChange={handleChangeSbpAccountNumber} />
						<LoginField settingsId={settingsId} onChange={handleChangeSbpLogin} />
					</div>
				)}
				{acquirerId === YANDEX_PAY_QR_BANK_SETTINGS_ID && (
					<>
						<YandexApiKeyField settingsId={settingsId} onChange={handleChangeYandexApiKey} />
						<YandexPaymentTypeSelect
							settingsId={settingsId}
							selectedYandexPaymentTypes={selectedYandexPaymentTypes}
							onChange={handleChangeYandexPaymentType}
						/>
					</>
				)}
				{acquirerId === VIVA_WALLET_SOFT_POS_QR_BANK_SETTINGS_ID && (
					<div className={style.row}>
						<VivaApiKeyField onChange={handleChangeVivaApiKey} settingsId={settingsId} />
						<VivaApiSecretField onChange={handleChangeVivaApiSecret} settingsId={settingsId} />
					</div>
				)}
				{acquirerId === BEST_2_PAY_QR_BANK_SETTINGS_ID && (
					<>
						<div className={style.row}>
							<AccNumberField onChange={handleChangeAccNumber} settingsId={settingsId} />
							<SectorField onChange={handleChangeSector} settingsId={settingsId} />
						</div>
						<div className={style.row}>
							<SdRefField onChange={handleChangeSdRef} settingsId={settingsId} />
							<div className={style.flex1} />
						</div>
					</>
				)}
				{acquirerId === YANDEX_CHECK_QR_BANK_SETTINGS_ID && (
					<div className={style.row}>
						<YandexChequeApiTokenField
							onChange={handleChangeYandexChequeApiToken}
							settingsId={settingsId}
						/>
						<YandexChequeApiPartnerIdField
							onChange={handleChangeYandexChequeApiPartnerId}
							settingsId={settingsId}
						/>
					</div>
				)}
				{acquirerId === ADYEN_BANK_SETTINGS_ID && (
					<>
						<div className={style.row}>
							<MerchantAccountField onChange={handleChangeMerchantAccount} settingsId={settingsId} />
							<StoreField onChange={handleChangeStore} settingsId={settingsId} />
						</div>
						<div className={style.row}>
							<XApiKeyField onChange={handleChangeXApiKey} settingsId={settingsId} />
							<CheckoutUrlPrefixField onChange={handleCheckoutUrlPrefix} settingsId={settingsId} />
						</div>
						<div className={style.row}>
							<div className={style.flex1}>
								<SplitPayments3SCheckbox
									settingsId={settingsId}
									splitPayments3S={splitPayments3S}
									onToggleSplitPayments3S={handleToggleSplitPayments3S}
								/>
							</div>
							<div className={style.flex1} />
						</div>
						{splitPayments3S && (
							<div className={style.row}>
								<MerchantBalanceAccountIdField
									onChange={handleChangeMerchantBalanceAccountId}
									settingsId={settingsId}
								/>
								<KmBalanceAccountIdField
									onChange={handleChangeKmBalanceAccountId}
									settingsId={settingsId}
								/>
							</div>
						)}
					</>
				)}
				{acquirerId === TOUCAN_BANK_SETTINGS_ID && (
					<MasterWalletField settingsId={settingsId} onChange={handleChangeMasterWallet} />
				)}
				{acquirerId === TBANK_BANK_SETTINGS_ID && (
					<div className={style.row}>
						<TBankPlaceIdField settingsId={settingsId} onChange={handleChangeTBankPlaceId} />
						<div className={style.flex1} />
					</div>
				)}
			</div>
			<FileBlock
				settingsId={settingsId}
				setBankSettingFile={setBankSettingFile}
				deleteBankSettingsFIle={deleteBankSettingFile}
				setLogoFile={setLogoFile}
				onImageFileSizeExceeded={handleImageFileSizeExceeded}
				bankSettingsFilePath={bankSettingsFilePath}
				logoFilePath={logoFilePath}
			/>
			<div className={style.divider} />
		</div>
	);
};
